/* eslint-disable react/jsx-filename-extension */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  withStyles,
} from '@material-ui/core';
import AgentChatManager from '@src/logic/agentchatmanager';
import _ from 'lodash';
import { Component } from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

const intlMessages = defineMessages({
  dialogTitle: {
    id: 'AwaitingChatDialog.title',
  },
  okButton: {
    id: 'AwaitingChatDialog.okButton',
  },
  pickupIncomingChatButton: {
    id: 'AwaitingChatDialog.pickupIncomingChatButton',
  },
  cancelButton: {
    id: 'AwaitingChatDialog.cancelButton',
  },
});

class AwaitingChatDialog extends Component {
  constructor(props) {
    super(props);

    this.handleResetChatRequest = this.handleResetChatRequest.bind(this);
    this.handleConfirmPickupExistingChat =
      this.handleConfirmPickupExistingChat.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { latestStartChatRequest, history } = this.props;
    if (
      latestStartChatRequest?.chatId !== null &&
      latestStartChatRequest?.chatId !==
        prevProps.latestStartChatRequest?.chatId
    ) {
      history.push('/agent/chat');
    }
  }

  handleResetChatRequest() {
    const { onResetAgentStartedChat } = this.props;
    onResetAgentStartedChat();
  }

  handleConfirmPickupExistingChat(chatId) {
    const {
      onRequestChatPickup,
      onResetAgentStartedChat,
      onChatsOnHoldFocused,
      history,
    } = this.props;
    onRequestChatPickup({ chatId });
    onResetAgentStartedChat();
    onChatsOnHoldFocused();
    history.push('/agent/chat');
  }

  render() {
    const { intl, latestStartChatRequest, classes } = this.props;
    const { formatMessage } = intl;

    let latestStartChatState = null;
    let existingChatId = null;
    let existingChatStatus = null;
    let isDialogOpen = null;
    let dialogContents = null;
    let dialogActions = null;

    if (!_.isNil(latestStartChatRequest)) {
      existingChatId = _.get(
        latestStartChatRequest,
        'errorDetails.existingChatId',
        null
      );

      existingChatStatus = _.get(
        latestStartChatRequest,
        'errorDetails.existingChatStatus',
        null
      );

      latestStartChatState = latestStartChatRequest.state;
    }

    // Render the dialog according to the current state
    if (
      latestStartChatState ===
      AgentChatManager.START_CHAT_REQUEST_STATES.REQUESTED
    ) {
      isDialogOpen = true;

      dialogContents = (
        <FormattedMessage id="AwaitingChatDialog.requestingChatLbl" />
      );
    } else if (
      latestStartChatState ===
      AgentChatManager.START_CHAT_REQUEST_STATES.AWAITING
    ) {
      isDialogOpen = true;

      dialogContents = (
        <FormattedMessage id="AwaitingChatDialog.awaitingChatLbl" />
      );
    } else if (
      latestStartChatState === AgentChatManager.START_CHAT_REQUEST_STATES.FAILED
    ) {
      const { errorCode, errorDetails, timestamp, rqId } =
        latestStartChatRequest;

      isDialogOpen = true;

      if (errorCode === 'CONTACT_ALREADY_IN_CHAT') {
        // Extract details about the error
        const contact = errorDetails?.contactFullName
          ? errorDetails.contactFullName
          : '';
        const agent = errorDetails?.curInChatWithAgentName
          ? errorDetails.curInChatWithAgentName
          : '';

        // Check if the existing chat is an incoming chat (not assigned to an agent) and show the appropriate dialog
        if (existingChatStatus === 'OPENED') {
          dialogContents = (
            <FormattedMessage
              id="AwaitingChatDialog.chatFailedAlreadyInIncomingChatLbl"
              values={{ contact: <span>{contact}</span> }}
            />
          );

          dialogActions = [
            <Button
              key="dialogActions-cancel"
              onClick={this.handleResetChatRequest}
              variant="outlined"
              color="primary"
            >
              {formatMessage(intlMessages.cancelButton)}
            </Button>,
            <Button
              key="dialogActions-pickup"
              color="primary"
              variant="contained"
              onClick={() =>
                this.handleConfirmPickupExistingChat(existingChatId)
              }
            >
              {formatMessage(intlMessages.pickupIncomingChatButton)}
            </Button>,
          ];
        } else {
          dialogContents = (
            <FormattedMessage
              id="AwaitingChatDialog.chatFailedAlreadyInChatLbl"
              values={{
                contact: <span>{contact}</span>,
                agent: <span>{agent}</span>,
                rqId: <span>{rqId}</span>,
              }}
            />
          );
        }
      } else if (errorCode === 'BLOCKED_BY_CONTACT') {
        // Extract details about the error
        const contactAccount = errorDetails?.contactAccountUsername
          ? errorDetails.contactAccountUsername
          : '';

        dialogContents = (
          <FormattedMessage
            id="AwaitingChatDialog.chatFailedBlockedByContact"
            values={{
              contactAccount: <span>{contactAccount}</span>,
              rqId: <span>{rqId}</span>,
            }}
          />
        );
      }

      // Last case: could not determine causes of failure
      if (dialogContents === null) {
        dialogContents = (
          <div>
            <FormattedMessage id="AwaitingChatDialog.chatFailedLbl" />
            <br />
            <br />
            <span style={{ fontSize: '0.75em', fontStyle: 'italic' }}>
              <FormattedMessage
                id="AwaitingChatDialog.chatFailedInfoLbl"
                values={{
                  code: errorCode,
                  details: errorDetails ? JSON.stringify(errorDetails) : '',
                  time: timestamp,
                  rqId: <span>{rqId}</span>,
                }}
              />
            </span>
          </div>
        );
      }

      if (dialogActions === null) {
        dialogActions = (
          <Button
            key="dialogActions-ok"
            color="primary"
            variant="contained"
            onClick={this.handleResetChatRequest}
          >
            {formatMessage(intlMessages.okButton)}
          </Button>
        );
      }
    } else {
      isDialogOpen = false;
    }

    return (
      <Dialog
        open={isDialogOpen}
        title={formatMessage(intlMessages.dialogTitle)}
      >
        <DialogTitle>
          <Box fontSize="16px" fontWeight={600}>
            {formatMessage(intlMessages.dialogTitle)}
          </Box>
        </DialogTitle>
        <DialogContent classes={{ root: classes.root }}>
          {dialogContents}
        </DialogContent>
        <DialogActions>
          <Box className={classes.actions}>{dialogActions}</Box>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withRouter(
  withStyles(theme => ({
    root: {
      fontWeight: 500,
      color: theme.b2chatorange[600],
      padding: '25px 30px',
    },
    actions: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      gap: 16,
    },
  }))(injectIntl(AwaitingChatDialog))
);
